import React, { useEffect, useRef } from 'react';
import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { EASE_OUT } from './tokens';
import usePrefersReducedMotion from './usePrefersReducedMotion';

function parseNumeric(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').replace(/\+/g, '');
    const n = Number(cleaned);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function formatValue(n, decimals, format, suffix) {
  if (typeof format === 'function') return format(n);
  const rounded =
    decimals > 0
      ? Number(n).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      : Math.round(n).toLocaleString();
  return `${rounded}${suffix}`;
}

export default function AnimatedNumber({
  value,
  className = '',
  format,
  decimals = 0,
  as: Component = 'span',
  from = 0,
}) {
  const reduced = usePrefersReducedMotion();
  const numeric = parseNumeric(value);
  const suffix =
    typeof value === 'string' && String(value).trim().endsWith('+') ? '+' : '';
  const startedRef = useRef(false);

  const count = useMotionValue(from);
  const display = useTransform(count, (latest) =>
    formatValue(latest, decimals, format, suffix),
  );

  useEffect(() => {
    if (numeric == null) return undefined;
    if (reduced) {
      count.set(numeric);
      startedRef.current = true;
      return undefined;
    }

    if (!startedRef.current) {
      count.set(from);
      startedRef.current = true;
    }

    const current = count.get();
    const delta = Math.abs(numeric - current);
    const duration = Math.min(1.05, Math.max(0.55, delta * 0.045 + 0.35));
    const controls = animate(count, numeric, {
      duration,
      ease: EASE_OUT,
    });
    return () => controls.stop();
  }, [numeric, reduced, count, decimals, from]);

  const MotionTag = motion[Component] || motion.span;
  const classes = `tabular-nums ${className}`.trim();

  if (numeric == null) {
    return <Component className={classes}>{value}</Component>;
  }

  if (reduced) {
    return (
      <Component className={classes}>
        {formatValue(numeric, decimals, format, suffix)}
      </Component>
    );
  }

  return <MotionTag className={classes}>{display}</MotionTag>;
}
